import { getPublicKey, handshake } from '@/api/encryption';
import {
    AES_IV_LENGTH,
    ENCRYPTION_ENABLED,
    ENCRYPTION_VERSION,
    ENDPOINT_HANDSHAKE,
    ENDPOINT_PUBLIC_KEY,
    HEADER_NONCE,
    HEADER_SESSION_KEY_ID,
    HEADER_TIMESTAMP,
    HEADER_VERSION
} from '@/utils/constants/encryption';
import {
    arrayBufferToBase64,
    arrayBufferToString,
    base64ToArrayBuffer,
    decryptAesGcm,
    deriveSessionKey,
    encryptAesGcm,
    exportClientPublicKeyJwk,
    generateEcdhKeyPair,
    generateIv,
    generateNonce,
    importServerPublicKey,
    jsonToString,
    parseJson,
    stringToArrayBuffer
} from '@/utils/crypto';
import type { EncryptedEnvelope, HandshakeResponse, JwkPublicKey, PublicKeyResponse } from '@/types/crypto';

let sessionKey: CryptoKey | null = null;
let sessionKeyId: string | null = null;
let handshakePromise: Promise<void> | null = null;

/**
 * 是否需要对当前 URL 进行加密
 */
export function shouldEncrypt(url?: string): boolean {
    if (!ENCRYPTION_ENABLED) return false;
    if (!url) return false;
    return !url.includes(ENDPOINT_PUBLIC_KEY) && !url.includes(ENDPOINT_HANDSHAKE);
}

/**
 * 获取加密请求头
 */
export function buildEncryptionHeaders(): Record<string, string> {
    return {
        [HEADER_VERSION]: ENCRYPTION_VERSION,
        [HEADER_SESSION_KEY_ID]: sessionKeyId || '',
        [HEADER_NONCE]: generateNonce(),
        [HEADER_TIMESTAMP]: String(Date.now())
    };
}

/**
 * 建立加密会话（握手）
 */
async function performHandshake(): Promise<void> {
    const publicKeyRes = await getPublicKey();
    if (String(publicKeyRes.state) !== '1' || !publicKeyRes.result) {
        throw new Error(publicKeyRes.msg || '获取服务端公钥失败');
    }
    const serverPubJwk = publicKeyRes.result as PublicKeyResponse;

    const keyPair = await generateEcdhKeyPair();
    const serverPublicKey = await importServerPublicKey({
        kty: 'EC',
        crv: 'P-256',
        x: serverPubJwk.x,
        y: serverPubJwk.y
    });
    const derivedKey = await deriveSessionKey(keyPair.privateKey, serverPublicKey);

    const clientPublicKeyJwk = await exportClientPublicKeyJwk(keyPair);
    const handshakeRes = await handshake({
        clientPublicKey: clientPublicKeyJwk,
        version: ENCRYPTION_VERSION
    });
    if (String(handshakeRes.state) !== '1' || !handshakeRes.result) {
        throw new Error(handshakeRes.msg || '加密握手失败');
    }
    const handshakeResult = handshakeRes.result as HandshakeResponse;

    sessionKey = derivedKey;
    sessionKeyId = handshakeResult.sessionKeyId;
}

/**
 * 确保会话已建立（带并发合并）
 */
export async function ensureSession(): Promise<void> {
    if (sessionKey && sessionKeyId) return;
    if (handshakePromise) return handshakePromise;

    handshakePromise = performHandshake()
        .then(() => {
            // 成功保持会话
        })
        .catch((err) => {
            resetSession();
            throw err;
        })
        .finally(() => {
            handshakePromise = null;
        });
    return handshakePromise;
}

/**
 * 加密请求体
 */
export async function encryptRequestBody(data: unknown): Promise<EncryptedEnvelope> {
    if (!sessionKey) {
        throw new Error('会话密钥未建立');
    }
    const iv = generateIv();
    const plainText = jsonToString(data);
    const encrypted = await encryptAesGcm(sessionKey, iv, stringToArrayBuffer(plainText));
    return {
        iv: arrayBufferToBase64(iv),
        data: arrayBufferToBase64(encrypted)
    };
}

/**
 * 解密响应体
 */
export async function decryptResponseBody(envelope: EncryptedEnvelope): Promise<unknown> {
    if (!sessionKey) {
        throw new Error('会话密钥未建立');
    }
    if (!envelope.iv || !envelope.data) {
        throw new Error('加密响应格式错误');
    }
    const iv = base64ToArrayBuffer(envelope.iv);
    if (iv.byteLength !== AES_IV_LENGTH) {
        throw new Error('响应 IV 长度错误');
    }
    const cipher = base64ToArrayBuffer(envelope.data);
    const plain = await decryptAesGcm(sessionKey, iv, cipher);
    return parseJson(arrayBufferToString(plain));
}

/**
 * 判断响应是否为加密信封（而非明文业务错误或明文数据）
 */
export function isEncryptedEnvelope(data: unknown): data is EncryptedEnvelope {
    if (typeof data !== 'object' || data === null) return false;
    const obj = data as Record<string, unknown>;
    return typeof obj.iv === 'string' && typeof obj.data === 'string' && !('state' in obj);
}

/**
 * 重置会话（例如密钥失效时重试）
 */
export function resetSession(): void {
    sessionKey = null;
    sessionKeyId = null;
    handshakePromise = null;
}

/**
 * 是否已建立会话
 */
export function isSessionReady(): boolean {
    return sessionKey !== null && sessionKeyId !== null;
}
